/**
 * Design System - Style Utilities
 *
 * Comprehensive style helpers to eliminate hardcoded values
 */

import { colors } from '../tokens/colors';
import { spacing } from '../tokens/spacing';
import { typography } from '../tokens/typography';
import { dimensions } from '../tokens/dimensions';
import { borders } from '../tokens/borders';

/**
 * Typography style helpers
 */
export const textStyles = {
  // Headings
  h1: {
    fontSize: typography.size['5xl'], // 48px
    fontWeight: typography.weight.semibold, // 600
    lineHeight: typography.lineHeight['52'], // 52px
    fontFamily: typography.fonts.primary,
    letterSpacing: typography.letterSpacing['-0.5'],
    color: colors.typography.primary,
  },
  h2: {
    fontSize: typography.size['4xl'], // 36px
    fontWeight: typography.weight.semibold,
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  h3: {
    fontSize: typography.size['2xl'], // 24px
    fontWeight: typography.weight.bold,
    lineHeight: 'normal',
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  h4: {
    fontSize: typography.size.xl, // 20px
    fontWeight: typography.weight.semibold,
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  h5: {
    fontSize: typography.size.base, // 16px
    fontWeight: typography.weight.semibold,
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },

  // Body text
  bodyLarge: {
    fontSize: typography.size.xl, // 20px
    fontWeight: typography.weight.regular,
    lineHeight: 'normal',
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  body: {
    fontSize: typography.size.base, // 16px
    fontWeight: typography.weight.regular,
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  bodyMedium: {
    fontSize: typography.size.base,
    fontWeight: typography.weight.medium,
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  bodySemibold: {
    fontSize: typography.size.base, // 16px
    fontWeight: typography.weight.semibold,
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  caption: {
    fontSize: typography.size.sm, // 14px
    fontWeight: typography.weight.regular,
    fontFamily: typography.fonts.secondary,
    color: colors.typography.text,
  },
  captionSemibold: {
    fontSize: typography.size.sm, // 14px
    fontWeight: typography.weight.semibold,
    fontFamily: typography.fonts.secondary,
    lineHeight: typography.lineHeight['20'],
    color: colors.typography.text,
  },
  small: {
    fontSize: typography.size.xs, // 12px
    fontWeight: typography.weight.light,
    fontFamily: typography.fonts.primary,
    color: colors.typography['alt-offset'],
  },
  badge: {
    fontSize: '10px',
    fontWeight: typography.weight.bold,
    lineHeight: 'normal',
    fontFamily: typography.fonts.primary,
    color: colors.typography.alternate,
  },

  // Card text variants
  cardTitle: {
    fontSize: typography.size['2xl'], // 24px
    fontWeight: typography.weight.bold,
    lineHeight: 'normal',
    fontFamily: typography.fonts.primary,
    color: colors.typography.primary,
  },
  cardTitleOnDark: {
    fontSize: typography.size['2xl'],
    fontWeight: typography.weight.bold,
    lineHeight: 'normal',
    fontFamily: typography.fonts.primary,
    color: colors.typography['alt-offset'],
  },
  cardBody: {
    fontSize: typography.size.base,
    fontWeight: typography.weight.semibold,
    fontFamily: typography.fonts.primary,
    color: colors.typography['alt-offset'],
  },
  cardCaption: {
    fontSize: typography.size.xs,
    fontWeight: typography.weight.light,
    fontFamily: typography.fonts.primary,
    color: colors.typography['alt-offset'],
  },
};

/**
 * Layout style helpers
 */
export const layoutStyles = {
  section: {
    paddingTop: spacing[20],
    paddingBottom: spacing[20],
    paddingLeft: spacing[20],
    paddingRight: spacing[20],
  },
  sectionVertical: {
    paddingTop: spacing[14],
    paddingBottom: spacing[14],
    paddingLeft: spacing[20],
    paddingRight: spacing[20],
  },
  container: {
    maxWidth: dimensions.container.maxContent,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  containerAlt: {
    maxWidth: dimensions.container.maxContentAlt,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
};

/**
 * Card style helpers
 */
export const cardStyles = {
  base: {
    backgroundColor: colors.background.primary,
    borderColor: colors.stroke.light,
    borderWidth: borders.width.thin,
    borderStyle: 'solid' as const,
    borderRadius: borders.radius.xl,
  },
  padding: {
    none: {},
    sm: { padding: spacing[3] },
    md: { padding: spacing[6] },
    lg: { padding: spacing[8] },
  },
};

/**
 * Button style helpers
 */
export const buttonStyles = {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
    borderRadius: borders.radius.sm,
    fontSize: typography.size.base,
    fontWeight: typography.weight.semibold,
    fontFamily: typography.fonts.primary,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  sizes: {
    sm: {
      paddingLeft: spacing[3],
      paddingRight: spacing[3],
      paddingTop: spacing[1.5],
      paddingBottom: spacing[1.5],
    },
    md: {
      paddingLeft: spacing[4],
      paddingRight: spacing[4],
      paddingTop: spacing[2.5],
      paddingBottom: spacing[2.5],
    },
    lg: {
      paddingLeft: spacing[6],
      paddingRight: spacing[6],
      paddingTop: spacing[3],
      paddingBottom: spacing[3],
    },
  },
  variants: {
    primary: {
      backgroundColor: colors.brand.primary,
      color: colors.typography.primary,
    },
    secondary: {
      backgroundColor: colors.background.alternate,
      color: colors.typography.alternate,
    },
    outline: {
      backgroundColor: colors.background.primary,
      borderColor: colors.stroke.base,
      borderWidth: borders.width.thin,
      borderStyle: 'solid' as const,
      color: colors.typography.primary,
    },
  },
};

/**
 * Badge style helpers
 */
export const badgeStyles = {
  container: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(4px)',
    paddingLeft: spacing[1],
    paddingRight: spacing[2],
    paddingTop: spacing[1],
    paddingBottom: spacing[1],
    borderRadius: borders.radius.lg,
    borderWidth: borders.width.thin,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    borderStyle: 'solid' as const,
  },
  text: {
    fontSize: '10px',
    fontWeight: typography.weight.bold,
    lineHeight: 'normal',
    fontFamily: typography.fonts.primary,
    color: colors.typography.alternate,
  },
};

/**
 * Complete style presets for common elements
 */
export const stylePresets = {
  // Hero Section
  heroGreeting: {
    fontSize: '32px',
    fontWeight: typography.weight.semibold,
    lineHeight: 'normal',
    fontFamily: typography.fonts.primary,
    color: colors.typography.text,
    textAlign: 'center' as const,
  },
  heroImage: {
    width: dimensions.component.logoCircle.width,
    height: dimensions.component.logoCircle.height,
  },

  // Section headers
  sectionBadge: {
    backgroundColor: colors.brand.primary,
    paddingLeft: spacing[1],
    paddingRight: spacing[3],
    paddingTop: spacing[1],
    paddingBottom: spacing[1],
    borderRadius: borders.radius.lg,
    gap: spacing[2.5],
  },
  sectionBadgeInner: {
    backgroundColor: colors.brand.secondary,
    borderWidth: borders.width.thin,
    borderColor: 'rgba(0, 0, 0, 0.12)',
    borderStyle: 'solid' as const,
    padding: spacing[2],
    borderRadius: borders.radius.md,
    gap: spacing[2],
  },

  // Experience list
  quickLinksContainer: {
    width: dimensions.container.experienceList,
  },
  quickLinksInner: {
    width: dimensions.container.experienceListInner,
  },
  quickLinkCard: {
    width: dimensions.component.cardExperience.width,
    height: dimensions.component.cardExperience.height,
  },

  // Signature cards
  signatureCard: {
    width: dimensions.component.cardSignature.width,
    height: dimensions.component.cardSignature.height,
  },

  // Duration badge
  durationBadge: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(4px)',
    gap: spacing[1],
    padding: spacing[1],
    borderRadius: borders.radius.sm,
  },
  durationNumber: {
    width: dimensions.component.durationBadgeNumber.width,
    backgroundColor: 'rgba(241, 241, 239, 0.19)',
    padding: spacing[1],
    borderRadius: borders.radius.sm,
  },
};
